<template>
  <Teleport to="body">
    <Transition name="account-deactivation-notice">
      <div
        v-if="visible"
        class="account-deactivation-notice"
        role="alertdialog"
        aria-live="assertive"
        aria-labelledby="account-deactivation-title"
        aria-describedby="account-deactivation-desc"
      >
        <div class="account-deactivation-notice__icon" aria-hidden="true">
          <i class="bi bi-alarm"></i>
        </div>
        <div class="account-deactivation-notice__body">
          <h4 id="account-deactivation-title" class="account-deactivation-notice__title">Account deactivated</h4>
          <p id="account-deactivation-desc" class="account-deactivation-notice__text">
            {{ message }} Save any work in progress. You will be signed out in
            <strong class="account-deactivation-notice__countdown">{{ countdownLabel }}</strong>.
          </p>
          <div class="account-deactivation-notice__actions">
            <PrimaryButton
              :label="signingOut ? 'Signing out...' : 'Log out now'"
              icon="bi bi-box-arrow-right"
              :loading="signingOut"
              compact
              @click="logoutNow"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue'
import { signOutDeactivatedAccount } from '@/services/companyPresenceClient'

export default {
  name: 'AccountDeactivationNotice',
  components: { PrimaryButton },
  data() {
    return {
      visible: false,
      message: '',
      deadline: 0,
      remaining: 0,
      signingOut: false,
    }
  },
  computed: {
    countdownLabel() {
      const s = Math.max(0, this.remaining)
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
    },
  },
  mounted() {
    this._onDeactivating = (e) => this.start((e && e.detail) || {})
    this._onCancelled = () => this.stop()
    window.addEventListener('client-account-deactivating', this._onDeactivating)
    window.addEventListener('client-account-deactivation-cancelled', this._onCancelled)
  },
  beforeUnmount() {
    window.removeEventListener('client-account-deactivating', this._onDeactivating)
    window.removeEventListener('client-account-deactivation-cancelled', this._onCancelled)
    this.clearTimer()
  },
  methods: {
    start(payload) {
      const seconds = Number(payload.graceSeconds) > 0 ? Number(payload.graceSeconds) : 60
      // Counted from arrival rather than the server's logoutAt, so clock skew does not matter.
      this.deadline = Date.now() + seconds * 1000
      this.message = payload.message || 'Your account has been deactivated.'
      this.visible = true
      this.tick()
      this.clearTimer()
      this._timer = setInterval(this.tick, 1000)
    },
    stop() {
      this.clearTimer()
      this.visible = false
    },
    clearTimer() {
      if (this._timer) {
        clearInterval(this._timer)
        this._timer = null
      }
    },
    tick() {
      this.remaining = Math.ceil((this.deadline - Date.now()) / 1000)
      // The server also signs the tab out; this covers a missed socket message.
      if (this.remaining <= 0) this.finish()
    },
    finish() {
      this.clearTimer()
      signOutDeactivatedAccount(`${this.message} You have been signed out.`)
    },
    async logoutNow() {
      this.signingOut = true
      this.clearTimer()
      try {
        await this.$store.dispatch('auth/logout')
      } finally {
        signOutDeactivatedAccount(`${this.message} You have been signed out.`)
      }
    },
  },
}
</script>

<style scoped>
/* Same frosted-glass look as SessionNearingExpiryModal, docked in a corner so work can continue. */
.account-deactivation-notice {
  --adn-ease: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 100048;
  display: flex;
  gap: 0.9rem;
  width: min(24rem, calc(100vw - 2rem));
  padding: 1.1rem 1.15rem 1rem;
  box-sizing: border-box;
  border-radius: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.78);
  background: linear-gradient(168deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 251, 235, 0.84) 42%,
      rgba(254, 243, 199, 0.8) 100%);
  backdrop-filter: blur(28px) saturate(1.12);
  -webkit-backdrop-filter: blur(28px) saturate(1.12);
  box-shadow:
    0 24px 60px -16px rgba(245, 158, 11, 0.28),
    0 14px 36px -12px rgba(99, 102, 241, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.92),
    0 0 0 1px rgba(251, 191, 36, 0.28);
}

.account-deactivation-notice__icon {
  flex: 0 0 auto;
  width: 2.6rem;
  height: 2.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1.1rem;
  color: #b45309;
  background: linear-gradient(145deg,
      rgba(255, 251, 235, 0.98) 0%,
      rgba(254, 215, 170, 0.92) 55%,
      rgba(251, 191, 36, 0.85) 100%);
  border: 1px solid rgba(251, 191, 36, 0.55);
  box-shadow: 0 8px 22px rgba(245, 158, 11, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.9);
  animation: account-deactivation-pulse 2.4s var(--adn-ease) infinite;
}

.account-deactivation-notice__body {
  flex: 1 1 auto;
  min-width: 0;
}

.account-deactivation-notice__title {
  margin: 0.1rem 0 0.35rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #312e81;
}

.account-deactivation-notice__text {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.55;
  color: #64748b;
}

.account-deactivation-notice__countdown {
  color: #b45309;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.account-deactivation-notice__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.8rem;
}

@keyframes account-deactivation-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 12px 28px rgba(245, 158, 11, 0.38), 0 0 0 6px rgba(251, 191, 36, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.95);
  }
}

.account-deactivation-notice-enter-active,
.account-deactivation-notice-leave-active {
  transition: opacity 0.3s var(--adn-ease), transform 0.3s var(--adn-ease);
}

.account-deactivation-notice-enter-from,
.account-deactivation-notice-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.97);
}

[data-ui-mode='compact'] .account-deactivation-notice__title {
  font-size: var(--ui-font-size-lg, 0.9rem);
}

[data-ui-mode='compact'] .account-deactivation-notice__text {
  font-size: var(--ui-font-size-sm, 0.75rem);
}

@media (max-width: 480px) {
  .account-deactivation-notice {
    right: 1rem;
    left: 1rem;
    bottom: 1rem;
    width: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .account-deactivation-notice__icon {
    animation: none !important;
  }

  .account-deactivation-notice-enter-active,
  .account-deactivation-notice-leave-active {
    transition: none !important;
  }
}
</style>
